import React, {useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faClock,
    faExternalLink,
    faImage,
    faMapLocationDot
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdMyDataSchema, EBirdObservationsBySpecies} from "ebird-mydata-reader";
import {useNavigate} from "react-router-dom";
import {getMacaulayCatalogNumbers} from "../../helpers/macaulayLibrary";
import {
    formatBirdLifeList,
    getBirdLifeSpeciesData,
    getRedListCategoryLabel
} from "../../helpers/birdlifeData";
import {
    getFamilyCommonName,
    getFamilyScientificName,
    getObservationLocation,
    getShortDate,
    getSpeciesMapUrl,
    getSpeciesObservationTime,
    titleCaseRange
} from "./checklist-helpers";
import './checklist-species-detail.scss';

export type ChecklistSpeciesDetailProps = {
    family: EBirdChecklistByFamily,
    species: EBirdChecklistBySpecies,
    observationsBySpecies: EBirdObservationsBySpecies[]
}

export function getSpeciesObservations(species: EBirdChecklistBySpecies, observationsBySpecies: EBirdObservationsBySpecies[]) {
    const taxonomicOrders = new Set<number>([
        species.taxonomicOrder,
        ...(species.alternateTaxonomicOrders ?? [])
    ]);

    return observationsBySpecies
        .filter(observationSpecies => taxonomicOrders.has(observationSpecies.taxonomicOrder))
        .flatMap(observationSpecies => observationSpecies.observations)
        .sort((a, b) => getSpeciesObservationTime(a) - getSpeciesObservationTime(b));
}

export function getSpeciesStats(species: EBirdChecklistBySpecies, observations: EBirdMyDataSchema[]) {
    const firstObservation = observations[0] ?? species.firstObservation;
    const latestObservation = observations[observations.length - 1] ?? species.firstObservation;
    const locationIds = new Set(observations.map(observation => observation.locationId || observation.location).filter(Boolean));
    const checklistIds = new Set(observations.map(observation => observation.submissionID ?? (observation as any).submissionId).filter(Boolean));
    const locations = Array.from(
        observations.reduce((locationMap, observation) => {
            const key = observation.locationId || observation.location;
            const existing = locationMap.get(key);
            if (!existing || getSpeciesObservationTime(observation) > getSpeciesObservationTime(existing)) {
                locationMap.set(key, observation);
            }
            return locationMap;
        }, new Map<string, EBirdMyDataSchema>()).values()
    ).sort((a, b) => getSpeciesObservationTime(b) - getSpeciesObservationTime(a));
    const photoCatalogNumbers = observations
        .flatMap(observation => getMacaulayCatalogNumbers(observation.mlCatalogNumbers))
        .filter((catalogNumber, index, all) => all.indexOf(catalogNumber) === index);

    return {
        firstObservation,
        latestObservation,
        observationCount: observations.length,
        checklistCount: checklistIds.size,
        locationCount: locationIds.size,
        recentObservations: [...observations].reverse().slice(0, 5),
        locations: locations.slice(0, 5),
        photoCatalogNumbers,
        mapUrl: getSpeciesMapUrl(observations)
    };
}

export type ChecklistSpeciesStats = ReturnType<typeof getSpeciesStats>;

export function ChecklistSpeciesDetail({family, species, observationsBySpecies}: ChecklistSpeciesDetailProps) {
    const navigate = useNavigate();
    const [activePhotoCatalogNumber, setActivePhotoCatalogNumber] = useState<string>();
    const speciesObservations = useMemo(() => {
        return getSpeciesObservations(species, observationsBySpecies);
    }, [observationsBySpecies, species]);
    const speciesStats = useMemo(() => {
        return getSpeciesStats(species, speciesObservations);
    }, [species, speciesObservations]);
    const eBirdUrl = `https://ebird.org/species/${species.speciesCode}`;
    const birdsOfTheWorldUrl = `https://birdsoftheworld.org/bow/species/${species.speciesCode}`;
    const birdLifeSpecies = getBirdLifeSpeciesData(species);
    const selectedPhoto = activePhotoCatalogNumber && speciesStats.photoCatalogNumbers.includes(activePhotoCatalogNumber)
        ? activePhotoCatalogNumber
        : speciesStats.photoCatalogNumbers[0];
    const mapUrl = `/map?year=life&mode=species&species=${species.speciesCode}`;
    const birdTypes = formatBirdLifeList([
        birdLifeSpecies?.landbird && 'Landbird',
        birdLifeSpecies?.waterbird && 'Waterbird',
        birdLifeSpecies?.seabird && 'Seabird'
    ]);
    const ecosystems = formatBirdLifeList([
        birdLifeSpecies?.terrestrial && 'Terrestrial',
        birdLifeSpecies?.freshwater && 'Freshwater',
        birdLifeSpecies?.marine && 'Marine'
    ]);

    useEffect(() => {
        setActivePhotoCatalogNumber(undefined);
    }, [species.speciesCode]);

    return (
        <div className="checklist__species-detail">
            <section className="checklist__species-grid" aria-label={`${species.commonName} details`}>
                {species.seen ?
                    <article className="checklist__species-card checklist__species-card--history">
                        <h2>Observation History</h2>
                        <div className="checklist__timeline">
                            {speciesStats.firstObservation ?
                                <div className="checklist__timeline-feature">
                                    <span><FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" /></span>
                                    <div>
                                        <strong>First Sighting</strong>
                                        <p>{getShortDate(speciesStats.firstObservation.date)} • {getObservationLocation(speciesStats.firstObservation)}</p>
                                    </div>
                                    {speciesStats.firstObservation.count ? <b>Count: {speciesStats.firstObservation.count}</b> : null}
                                </div>
                                : null
                            }
                            {speciesStats.observationCount > 1 && speciesStats.latestObservation ?
                                <div className="checklist__timeline-feature">
                                    <span><FontAwesomeIcon icon={faClock} aria-hidden="true" /></span>
                                    <div>
                                        <strong>Most Recent</strong>
                                        <p>{getShortDate(speciesStats.latestObservation.date)} • {getObservationLocation(speciesStats.latestObservation)}</p>
                                    </div>
                                    {speciesStats.latestObservation.count ? <b>Count: {speciesStats.latestObservation.count}</b> : null}
                                </div>
                                : null
                            }
                        </div>
                        {speciesStats.observationCount > 1 && speciesStats.recentObservations.length ?
                            <section className="checklist__recent-observations" aria-label="Recent observations">
                                <h3>Recent Observations</h3>
                                <ol>
                                    {speciesStats.recentObservations.map((observation, index) => (
                                        <li key={`${observation.submissionID ?? (observation as any).submissionId}-${observation.locationId}-${index}`}>
                                            <time>{getShortDate(observation.date)}</time>
                                            <span>{observation.time || 'All day'}</span>
                                            <strong>{observation.location}</strong>
                                            <b>Count: {observation.count}</b>
                                        </li>
                                    ))}
                                </ol>
                            </section>
                            : null
                        }
                        {speciesStats.mapUrl ?
                            <a className="checklist__history-map-link" href={mapUrl} aria-label={`View all ${species.commonName} sightings on a map`}>
                                <img className="checklist__history-map" src={speciesStats.mapUrl} alt="" />
                            </a>
                            : null
                        }
                        <button
                            type="button"
                            className="checklist__primary-cta checklist__history-map-cta"
                            onClick={() => navigate(mapUrl)}
                        >
                            <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" />
                            View All Sightings on a Map
                        </button>
                    </article>
                    : null
                }

                <div className="checklist__species-side">
                    <article className="checklist__species-card checklist__species-card--about">
                        <h2>About This Species</h2>
                        <dl className="checklist__about-list">
                            <div>
                                <dt>Family</dt>
                                <dd>{species.familyCommon || getFamilyCommonName(family)} ({species.familyScientific || getFamilyScientificName(family)})</dd>
                            </div>
                            <div>
                                <dt>Range</dt>
                                <dd>{titleCaseRange(species.range) || 'Unknown'}</dd>
                            </div>
                            <div>
                                <dt>Conservation Status</dt>
                                <dd>
                                    {getRedListCategoryLabel(birdLifeSpecies?.redListCategory)}
                                    {birdLifeSpecies?.possiblyExtinct ? ' (Possibly Extinct)' : ''}
                                    {birdLifeSpecies?.possiblyExtinctInTheWild ? ' (Possibly Extinct in the Wild)' : ''}
                                </dd>
                            </div>
                            <div>
                                <dt>Population Trend</dt>
                                <dd>{birdLifeSpecies?.populationTrend || 'Not Available'}{birdLifeSpecies?.populationTrendDerivation ? ` (${birdLifeSpecies.populationTrendDerivation})` : ''}</dd>
                            </div>
                            <div>
                                <dt>Population Size</dt>
                                <dd>{birdLifeSpecies?.populationSize || 'Not Available'}{birdLifeSpecies?.populationSizeDerivation ? ` (${birdLifeSpecies.populationSizeDerivation})` : ''}</dd>
                            </div>
                            <div>
                                <dt>Migratory Status</dt>
                                <dd>{birdLifeSpecies?.migratoryStatus || 'Not Available'}</dd>
                            </div>
                            <div>
                                <dt>Bird Type</dt>
                                <dd>{birdTypes}</dd>
                            </div>
                            <div>
                                <dt>Ecosystems</dt>
                                <dd>{ecosystems}</dd>
                            </div>
                            {birdLifeSpecies?.criteria ?
                                <div>
                                    <dt>Red List Criteria</dt>
                                    <dd>{birdLifeSpecies.criteria}</dd>
                                </div>
                                : null
                            }
                            {birdLifeSpecies?.areaOfOccupancy && birdLifeSpecies.areaOfOccupancy !== '0' ?
                                <div>
                                    <dt>Area of Occupancy</dt>
                                    <dd>{Number(birdLifeSpecies.areaOfOccupancy).toLocaleString()} km²</dd>
                                </div>
                                : null
                            }
                            {birdLifeSpecies?.extentOfOccurrence && birdLifeSpecies.extentOfOccurrence !== '0' ?
                                <div>
                                    <dt>Extent of Occurrence</dt>
                                    <dd>{Number(birdLifeSpecies.extentOfOccurrence).toLocaleString()} km²</dd>
                                </div>
                                : null
                            }
                            <div>
                                <dt>Source</dt>
                                <dd>BirdLife International 2026</dd>
                            </div>
                        </dl>
                        <div className="checklist__cta-row">
                            <a className="checklist__primary-cta" href={eBirdUrl} target="_blank" rel="noreferrer">
                                View on eBird
                                <FontAwesomeIcon icon={faExternalLink} aria-hidden="true" />
                            </a>
                            <a className="checklist__secondary-cta" href={birdsOfTheWorldUrl} target="_blank" rel="noreferrer">
                                Birds of the World
                                <FontAwesomeIcon icon={faExternalLink} aria-hidden="true" />
                            </a>
                        </div>
                    </article>

                    {speciesStats.photoCatalogNumbers.length ?
                        <article className="checklist__species-card checklist__species-card--photos">
                            <h2>Your Photos ({speciesStats.photoCatalogNumbers.length})</h2>
                            <div className="checklist__photo-browser">
                                {selectedPhoto ?
                                    <iframe
                                        className="checklist__photo-embed"
                                        src={`https://macaulaylibrary.org/asset/${selectedPhoto}/embed`}
                                        title={`Macaulay Library asset ML${selectedPhoto}`}
                                        loading="lazy"
                                    />
                                    : null
                                }
                                <ul className="checklist__photo-list">
                                    {speciesStats.photoCatalogNumbers.slice(0, 8).map(catalogNumber => (
                                        <li key={catalogNumber}>
                                            <button
                                                type="button"
                                                className={catalogNumber === selectedPhoto ? 'is-active' : ''}
                                                onClick={() => setActivePhotoCatalogNumber(catalogNumber)}
                                            >
                                                <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                                ML{catalogNumber}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                        : null
                    }
                </div>
            </section>

        </div>
    );
}
